import React, { useState, useEffect } from 'react';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Upload,
  message,
  notification,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { editAssessment, setRoomEditData, setRoomErrorData } from '@/actions/room';
import { getSubjects } from '@/actions/subjects';
import FormDataHelper, { blockInvalidChar } from '@/helpers/formDataHelper';
import { removeImageKeyFromObj } from '@/helpers/dataHelper';
import moment from 'moment';
import { disabledDate } from '@/helpers/dateHelper';
import TinymceEditor from '@/helpers/TinyMceEditor';
import { UploadOutlined } from '@ant-design/icons';
import { getDeviceState } from '@/components/landing/live/utils';
import { resetTimer, startTimer, stopTimer } from '@/helpers/timeHelper';

const EditAssessment = ({ roomSlug, roomId, courseSlug, handleCancel, data }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const dateFormat = 'YYYY-MM-DD HH:mm:ss';

  const [file, setfile] = useState([]);
  const [audioRecording, setAudioRecording] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [dueDate, setDueDate] = useState(null);
  const [description, setDescription] = useState(null);
  const [devices, setdevices] = useState({ mic: null, camera: null });

  const { error, subjects, assessment, assessmentData } = useSelector(
    (state: any) => ({
      error: state?.roomReducer?.error?.addAssessment,
      subjects: state?.subjects?.subjects,
      assessment: state?.roomReducer?.assessment,
      assessmentData: state?.roomReducer?.addAssessment,
    })
  );

  useEffect(() => {
    form.setFieldsValue({
      title: data?.title,
      subject: data?.subject?.id,
      due_date: data?.due_date ? moment(data?.due_date) : '',
      description: data?.description,
      total_marks: data?.total_marks,
    });
    setDueDate(data?.due_date !== null ? data?.due_date : '');
    setDescription(data?.description);
    setAudioRecording(null);
    dispatch(
      setRoomEditData({ name: 'assessment', key: 'editAssessmentStatus', value: '' })
    );
  }, [data]);

  useEffect(() => {
    if (assessment?.editAssessmentStatus === 'success') {
      form.resetFields();
      setfile([]);
      setIsRecording(false);
      setAudioRecording(null);
      dispatch(
        setRoomEditData({
          name: 'assessment',
          key: 'editAssessmentStatus',
          value: '',
        })
      );
      handleCancel();
      notification.success({
        message: 'Assessment edited successfully',
      });
    }
  }, [assessment?.editAssessmentStatus]);

  useEffect(() => {
    courseSlug && dispatch(getSubjects(courseSlug));
  }, [courseSlug]);

  const onFinish = async (values: any) => {
    let value;
    const datas = {
      ...values,
      room: roomId,
      due_date: dueDate,
      description: assessmentData?.description,
      file_type: file?.length ? 0 : audioRecording && 1,
    };
    delete datas['files'];
    !dueDate && delete datas['due_date'];
    if (file || audioRecording) {
      let temp = file;
      if (audioRecording) {
        const blobFile = new File(
          [audioRecording],
          `${assessmentData?.title}-voice.wav`
        );
        temp.push(blobFile);
      }
      value = FormDataHelper({ ...datas }, { files: temp });
    } else {
      let imgRemoval = removeImageKeyFromObj({ ...datas }, 'files');
      value = FormDataHelper(imgRemoval, {});
    }
    dispatch(editAssessment(value, data?.id));
  };

  const dueDateChange = (date, dateString) => {
    setDueDate(dateString);
    dispatch(
      setRoomEditData({ name: 'addAssessment', key: 'due_date', value: dateString })
    );
    dispatch(
      setRoomErrorData({ name: 'addAssessment', key: 'due_date', value: '' })
    );
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    dispatch(setRoomEditData({ name: 'addAssessment', key: name, value: value }));
    dispatch(setRoomErrorData({ name: 'addAssessment', key: name, value: '' }));
  };

  const handleSuccess = function (stream) {
    const stopButton: any = document.getElementById('stop');
    const options = { mimeType: 'audio/webm' };
    let recordedChunks = [];
    const mediaRecorder = new MediaRecorder(stream, options);

    mediaRecorder.addEventListener('dataavailable', function (e) {
      if (e.data.size > 0) recordedChunks.push(e.data);
    });
    mediaRecorder.addEventListener('stop', function () {
      const url = URL.createObjectURL(
        new Blob(recordedChunks, { type: 'audio/ogg' })
      );
      setAudioRecording(url);
      setIsRecording(false);
      recordedChunks = [];
      const player: any = document.getElementById('player');
      player.src = url;
      const blobFile = new File([url], `${assessmentData?.title}-voice.wav`);
    });
    stopButton.addEventListener('click', function () {
      mediaRecorder.stop();
      stopTimer();
      resetTimer();
      var element = document.getElementById('stopwatch');
      element.style.display = 'none';
    });

    mediaRecorder.start();
  };

  const handleSelect = (name: any, val: any) => {
    dispatch(setRoomEditData({ name: 'addAssessment', key: name, value: val }));
    dispatch(setRoomErrorData({ name: 'addAssessment', key: name, value: '' }));
  };

  const getEditorData = (data, id) => {
    dispatch(setRoomEditData({ name: 'addAssessment', key: id, value: data }));
    dispatch(setRoomErrorData({ name: 'addAssessment', key: id, value: '' }));
  };

  const startRecording = () => {
    getDeviceState().then(({ hasCamera, hasMicrophone }) => {
      setdevices({ camera: hasCamera, mic: hasMicrophone });
    });
    if (devices?.mic) {
      var element = document.getElementById('stopwatch');
      element.style.display = 'block';

      const mediaDevices = navigator.mediaDevices as any;
      mediaDevices
        .getUserMedia({ audio: true })
        .then(function (stream) {
          setTimeout(() => startTimer(), 1000);
          setIsRecording(true);
          handleSuccess(stream);
        })
        .catch(function (err) {});
    } else {
      message.error('No microphone detected');
    }
  };

  const normFile = (e: any) => {
    let arr = [];
    e.fileList?.map((each) => arr.push(each.originFileObj));
    setfile(arr);
    dispatch(
      setRoomEditData({
        name: 'editAssessment',
        key: 'files',
        value: '',
      })
    );
    dispatch(setRoomErrorData({ name: 'editAssessment', key: 'files', value: '' }));
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  return (
    <Form form={form} onFinish={onFinish} id="edit-assessment">
      <Row>
        <Col span={24}>
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, message: 'Please input course title!' }]}
          >
            <Input
              name="title"
              onChange={handleInputChange}
              autoComplete="off"
              style={{ borderRadius: '5px' }}
            />
          </Form.Item>
          {error?.title && <div className="validation">{error?.title || ''}</div>}
        </Col>
      </Row>
      <Row>
        <Col span={10}>
          <Form.Item
            label="Subject"
            name="subject"
            rules={[{ required: true, message: 'Please select subject!' }]}
          >
            <Select
              showSearch
              placeholder="Select subject"
              onChange={(val) => handleSelect('subject', val)}
            >
              {subjects?.data &&
                subjects?.data.length !== 0 &&
                subjects?.data?.map((item) => (
                  <Select.Option value={item.id} key={item.slug}>
                    {item.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          {error?.subject && (
            <div className="validation">{error?.subject || ''}</div>
          )}
        </Col>
        <Col span={3} className="pl-2">
          <Form.Item label="Total Marks" name="total_marks">
            <Input
              type="number"
              name="total_marks"
              onChange={handleInputChange}
              autoComplete="off"
              onKeyDown={blockInvalidChar}
              style={{ borderRadius: '5px' }}
            />
          </Form.Item>
          {error?.total_marks && (
            <div className="validation">{error?.total_marks || ''}</div>
          )}
        </Col>
        <Col span={10} className="pl-2">
          <Form.Item label="Due Date" name="due_date">
            <DatePicker
              format={dateFormat}
              disabledDate={disabledDate}
              showTime={{ defaultValue: moment('00:00:00', 'HH:mm:ss') }}
              onChange={dueDateChange}
              style={{ borderRadius: '5px' }}
            />
          </Form.Item>
          {error?.due_date && (
            <div className="validation">{error?.due_date || ''}</div>
          )}
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <Form.Item label="Description" name="description">
            <TinymceEditor editorValue={getEditorData} value={description} />
          </Form.Item>
          {error?.description && (
            <div className="validation">{error?.description || ''}</div>
          )}
        </Col>
      </Row>

      <Row>
        <Col span={12} className="pb-0">
          <Form.Item label="File">
            <Form.Item
              name="files"
              valuePropName="fileList"
              getValueFromEvent={normFile}
            >
              <Upload listType="picture" className="upload-list-inline">
                <Button icon={<UploadOutlined rev="" />}>Upload</Button>
              </Upload>
            </Form.Item>
          </Form.Item>
          {error?.files && (
            <div className="validation mb-2">{error?.files || ''}</div>
          )}
        </Col>

        <Col span={12}>
          <Form.Item label="or Send voice">
            <input
              type="hidden"
              value="0"
              className="mic-box__speaker-radial"
              style={{ display: 'none', width: '0px', visibility: 'hidden' }}
            />
            <span
              className="cursor-pointer record-icon"
              onClick={startRecording}
              title="Click to start recording"
            >
              <i
                className={`${audioRecording ? 'mb-5' : ''} fas fa-microphone`}
                style={{ marginBottom: audioRecording ? '20px' : '' }}
              ></i>
            </span>
            {isRecording && (
              <>
                <span id="stop" className="ml-3 cursor-pointer record-icon">
                  <i className="far fa-stop-circle"></i>
                </span>
              </>
            )}
            <span id="stopwatch" className="ml-2"></span>
            <br />
            {audioRecording && !isRecording ? (
              <audio id="player" controls className="ml-3 mt-3">
                <source src={audioRecording} type="audio/mp3" />
              </audio>
            ) : (
              ''
            )}
          </Form.Item>
        </Col>
      </Row>
      <div className="d-flex justify-content-end ">
        <Form.Item className="m-0">
          <Button
            className="border-0 px-4 text-danger "
            key="cancel"
            onClick={() => {
              handleCancel();
              setfile([]);
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            className="px-5"
            style={{ borderRadius: '5px' }}
            loading={assessment?.editAssessmentStatus === 'request'}
          >
            Submit
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default EditAssessment;
